import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AccountsService } from '../../accounts.service';
import { AccountingTabLabelComponent } from '../../shared/accounting-tab-label.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PaymentProposal, PaymentProposalLine } from '../phase7-ap-controls.model';

@Component({
    selector: 'app-payment-proposal-detail',
    standalone: true,
    imports: [CommonModule, ButtonModule, TableModule, TabsModule, TagModule, ToastModule, AccountingTabLabelComponent, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent],
    providers: [MessageService],
    templateUrl: './payment-proposal-detail.component.html',
    styleUrls: ['./payment-proposal-detail.component.css']
})
export class PaymentProposalDetailComponent implements OnInit {
    proposal?: PaymentProposal;
    loading = false;
    activeTab = 'overview';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.load();
    }

    onTabChange(value: string | number): void {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'lines', 'excluded', 'payment'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    get readyLines(): PaymentProposalLine[] {
        return this.proposal?.lines?.filter(line => line.readinessStatus === 'READY') ?? [];
    }

    get excludedLines(): PaymentProposalLine[] {
        return this.proposal?.lines?.filter(line => line.readinessStatus !== 'READY') ?? [];
    }

    load(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) return;
        this.loading = true;
        this.accountsService.getPaymentProposal(id).subscribe({
            next: proposal => {
                this.proposal = proposal;
                this.loading = false;
            },
            error: err => this.fail('Proposal load failed', err)
        });
    }

    approve(): void {
        if (!this.proposal?.proposalId) return;
        this.accountsService.approvePaymentProposal(this.proposal.proposalId, this.actorId(), 'Approved from proposal detail').subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Approved', detail: 'Proposal approved.' }); this.load(); },
            error: err => this.fail('Approval failed', err)
        });
    }

    release(): void {
        if (!this.proposal?.proposalId) return;
        this.accountsService.releasePaymentProposal(this.proposal.proposalId, this.actorId(), 'Released from proposal detail').subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Released', detail: 'Proposal released.' }); this.load(); },
            error: err => this.fail('Release failed', err)
        });
    }

    vouchers(): void {
        if (!this.proposal?.proposalId) return;
        this.accountsService.generatePaymentProposalVouchers(this.proposal.proposalId).subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Vouchers generated', detail: 'Approved proposal lines were converted to vouchers.' }); this.load(); },
            error: err => this.fail('Voucher generation failed', err)
        });
    }

    remittance(): void {
        if (!this.proposal?.proposalId) return;
        this.accountsService.generatePaymentProposalRemittance(this.proposal.proposalId, this.actorId()).subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Remittance generated', detail: 'Remittance advice records created.' }); this.load(); },
            error: err => this.fail('Remittance failed', err)
        });
    }

    openBill(line: PaymentProposalLine): void {
        if (line.billId) this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', line.billId]);
    }

    back(): void {
        this.router.navigate(['/accounts/enterprise/payables/payment-proposals']);
    }

    severity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'READY' || status === 'APPROVED' || status === 'VOUCHERS_GENERATED') return 'success';
        if (status === 'DRAFT') return 'info';
        if (status === 'RELEASED' || status === 'BANK_UNVERIFIED' || status === 'PAYMENT_HELD') return 'warn';
        if (status === 'EXCLUDED' || status === 'MATCH_EXCEPTION' || status === 'NOT_APPROVED' || status === 'FULLY_PAID') return 'danger';
        return 'secondary';
    }

    money(amount?: number, currency = this.proposal?.currencyCode || 'SAR'): string {
        return `${currency} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    private fail(summary: string, err: any): void {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary, detail: err.error?.message || 'Operation failed.' });
    }

    private actorId(): number | undefined {
        const user = this.sessionStorageService.getUser();
        return user?.id ?? user?.userId ?? this.sessionStorageService.getCustomer()?.id;
    }
}
